import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  Wifi,
  Eye,
  Zap,
  ChevronRight,
  ArrowUpRight,
  Globe,
  Check,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { PlanCard } from "@/components/PlanCard";
import { PRODUCTS } from "@/lib/products";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const stats = [
  { value: "500M+", label: "Threats blocked monthly" },
  { value: "50M+", label: "Devices protected globally" },
  { value: "99.9%", label: "Malware detection rate" },
  { value: "24/7", label: "Live cyber-safety support" },
];

const features = [
  { icon: ShieldCheck, title: "Real-Time Threat Defense", body: "Multi-layered protection against ransomware, viruses and zero-day attacks." },
  { icon: Lock, title: "Identity Safeguards", body: "Dark web monitoring alerts you the moment your data appears for sale." },
  { icon: Wifi, title: "Secure VPN", body: "Bank-grade encryption for your Wi-Fi — at home, at work or on the go." },
  { icon: Eye, title: "Privacy Controls", body: "Stop trackers, fingerprinters and data harvesters before they ever load." },
  { icon: Zap, title: "PC Tune-Up", body: "Huntheal keeps your PC light — automatic definition updates and background scans that don't slow you down." },
  { icon: Globe, title: "Cross-Platform", body: "PC, Mac, iOS and Android — one subscription covers them all." },
];

const reviews = [
  { name: "Hannah K.", role: "Designer · Berlin", text: "Huntheal set up in three minutes and hasn't slowed my PC down since." },
  { name: "Marcus D.", role: "Freelance Dev · Austin", text: "Huntheal is the only antivirus I install on every new Windows machine. Just works." },
  { name: "Priya R.", role: "Small Biz Owner · Mumbai", text: "The ransomware shield saved us from a phishing wave. Worth every cent." },
];

const Home = () => {
  useDocumentMeta({
    title: "Buy Huntheal Antivirus for PC — From $34.95/Year",
    description:
      "Huntheal Antivirus for PC — real-time threat protection, ransomware shield and phishing defense. Plans from $34.95/year for 1 PC, $39.95 for 3 PCs, $44.95 for 5 PCs. Instant delivery with PayPal.",
  });

  // Inject FAQ JSON-LD for SEO rich-results
  useEffect(() => {
    const faqs = [
      ["How is my Huntheal Antivirus license delivered?", "Instantly after checkout. You'll see a confirmation page with your activation key and we'll also email it to you."],
      ["Which platforms does Huntheal support?", "Huntheal Antivirus is built for Windows PCs (Windows 10 and 11). Each license covers 1, 3, or 5 PCs depending on the plan you choose."],
      ["What's your refund policy?", "We offer a 30-day money-back guarantee on UNUSED Huntheal licenses. If the activation key hasn't been redeemed yet, call +1 (888) 655-4464 or email support@prosoftmarketplace.com within 30 days."],
      ["Is checkout secure?", "Checkout is processed by PayPal using bank-grade 256-bit TLS encryption. We never see or store your card details."],
      ["Are Huntheal licenses genuine?", "100% genuine. Every Huntheal key you purchase activates against the official Huntheal servers with a full year of definition updates."],
      ["How many PCs can one license protect?", "It depends on the plan: 1 PC ($34.95), 3 PCs ($39.95), or 5 PCs ($44.95) — all for one full year."],
    ];
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map(([q, a]) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    };
    let s = document.getElementById("home-faq-jsonld");
    if (!s) {
      s = document.createElement("script");
      s.id = "home-faq-jsonld";
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(data);
    return () => {
      const existing = document.getElementById("home-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  const huntheal = PRODUCTS[0];
  const featured = huntheal ? huntheal.plans : [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 border border-slate-900 bg-emerald-500 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-900"
            >
              <Star className="h-3 w-3 fill-current" /> Huntheal Antivirus · Trusted PC protection since 2026
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-6 font-display text-5xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl"
            >
              Cyber safety,<br />
              <span className="relative inline-block">
                <span className="relative z-10 px-2">made obvious.</span>
                <span className="absolute inset-x-0 bottom-1 z-0 h-4 bg-emerald-500" />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
            >
              The complete Huntheal Antivirus product line for Windows PCs — real-time threat defense,
              ransomware shield, phishing protection and a lightweight footprint. Choose your plan, pay
              with PayPal, get protected in minutes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <Button
                asChild
                data-testid="hero-shop-btn"
                className="rounded-sm bg-emerald-500 px-6 py-6 font-bold text-slate-900 hover:bg-emerald-400"
              >
                <Link to="/products">
                  Shop All Plans <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-compare-btn"
                className="rounded-sm border-slate-900 px-6 py-6 font-bold hover:bg-slate-900 hover:text-white"
              >
                <Link to="/products/huntheal-antivirus-pc">Compare Plans</Link>
              </Button>
            </motion.div>

            <div className="mt-10 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
              <span className="h-2 w-2 bg-emerald-500" />
              30-day money-back guarantee
              <span className="mx-3 text-slate-300">/</span>
              <span className="h-2 w-2 bg-emerald-500" /> Instant license delivery
            </div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative border border-slate-900 bg-slate-950 p-6 text-white shadow-[12px_12px_0_0_rgba(16,185,129,1)]"
            >
              <div className="flex items-center justify-between text-xs font-bold uppercase tracking-[0.2em]">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 animate-pulse bg-emerald-400" />
                  Live Protection
                </span>
                <span className="font-mono text-slate-400">v25.0.4</span>
              </div>
              <div className="mt-6 font-display text-4xl font-black tracking-tighter">
                You&apos;re protected.
              </div>
              <div className="mt-1 text-sm text-slate-400">
                Last scan: 3 min ago · 0 threats found
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                {[
                  { l: "Threats Blocked", v: "1,284" },
                  { l: "Devices Online", v: "5 / 5" },
                  { l: "VPN", v: "Frankfurt" },
                  { l: "Dark Web", v: "All Clear" },
                ].map((m) => (
                  <div key={m.l} className="border border-slate-800 p-3">
                    <div className="text-slate-500">{m.l}</div>
                    <div className="mt-1 font-display text-lg font-black text-emerald-500">
                      {m.v}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 border border-emerald-500 bg-emerald-500/10 p-3 text-xs">
                <div className="font-bold uppercase tracking-[0.2em] text-emerald-500">
                  Recommended
                </div>
                <div className="mt-1 text-slate-200">
                  Enable Privacy Monitor for 3 inactive data brokers.
                </div>
              </div>
            </motion.div>

            <div className="absolute -bottom-4 -left-4 -z-10 h-full w-full border border-slate-200" />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-slate-200 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:divide-x lg:px-8">
          {stats.map((s) => (
            <div key={s.label} className="px-4 py-2">
              <div className="font-display text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
                {s.value}
              </div>
              <div className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PLANS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Step 01 / Choose
            </div>
            <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Three simple plans. One year of Huntheal Antivirus.
            </h2>
          </div>
          <Button
            asChild
            variant="ghost"
            data-testid="view-all-products-btn"
            className="rounded-sm font-bold uppercase tracking-[0.15em] hover:bg-slate-100"
          >
            <Link to="/products/huntheal-antivirus-pc">
              Full product details <ArrowUpRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featured.map((plan) => (
            <PlanCard
              key={plan.id}
              product={huntheal}
              plan={plan}
              highlighted={plan.id === "huntheal-3pc"}
              features={huntheal.features}
            />
          ))}
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Step 02 / Protect
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Every layer of defense, in one subscription.
              </h2>
              <p className="mt-4 text-base text-slate-600">
                Huntheal bundles real-time defense, ransomware shield, phishing protection and a smart firewall
                — everything your PC needs, without the bloat.
              </p>
              <Button
                asChild
                data-testid="features-cta-btn"
                className="mt-6 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
              >
                <Link to="/products">Compare Plans</Link>
              </Button>
            </div>

            <div className="grid gap-4 lg:col-span-8 lg:grid-cols-2">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="group border border-slate-200 bg-white p-6 transition-all hover:border-slate-900 hover:shadow-[6px_6px_0_0_rgba(15,23,42,1)]"
                >
                  <div className="flex h-10 w-10 items-center justify-center bg-emerald-500">
                    <f.icon className="h-5 w-5 text-slate-900" strokeWidth={2.5} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/7191166/pexels-photo-7191166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Secure checkout"
              className="w-full border border-slate-900"
            />
            <div className="absolute -bottom-4 -right-4 -z-10 h-full w-full bg-emerald-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Step 03 / Checkout
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Pay with PayPal. Get protected in minutes.
            </h2>
            <p className="mt-4 text-slate-600">
              No long forms. No credit card juggling. Add what you need to your cart and check out
              securely using your PayPal account — your license keys are delivered instantly.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Encrypted PayPal checkout",
                "Instant license activation",
                "30-day money-back guarantee",
                "24/7 customer support",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center bg-slate-900">
                    <Check className="h-4 w-4 text-emerald-500" strokeWidth={3} />
                  </span>
                  <span className="text-slate-800">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="border-t border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-500">
                Trusted by millions
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Reviewed 4.8 / 5 across 12,000+ users.
              </h2>
            </div>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={`hero-star-${i}`} className="h-5 w-5 fill-emerald-500 text-emerald-500" />
              ))}
            </div>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {reviews.map((r) => (
              <div key={r.name} className="border border-slate-800 bg-slate-900 p-6">
                <div className="flex items-center gap-1 text-emerald-500">
                  {[...Array(5)].map((_, j) => (
                    <Star key={`${r.name}-star-${j}`} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-200">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-6 border-t border-slate-800 pt-4 text-xs">
                  <div className="font-bold text-white">{r.name}</div>
                  <div className="text-slate-500">{r.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Questions
        </div>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Frequently asked.
        </h2>
        <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
          {[
            {
              q: "How is my Huntheal license delivered?",
              a: "Instantly after checkout. You'll see a confirmation page with your activation key and we'll also email it to you. Most customers are protected within 5 minutes of payment.",
            },
            {
              q: "Which operating systems does Huntheal support?",
              a: "Huntheal Antivirus is built for Windows 10 and Windows 11 PCs. It's optimized to run smoothly on both older laptops and modern gaming rigs.",
            },
            {
              q: "What's your refund policy?",
              a: "We offer a 30-day money-back guarantee on UNUSED Huntheal licenses. If your activation key hasn't been redeemed yet, just call +1 (888) 655-4464 or email support@prosoftmarketplace.com within 30 days of purchase and we'll process a full refund.",
            },
            {
              q: "Is checkout secure?",
              a: "Checkout is processed by PayPal using bank-grade 256-bit TLS encryption. We never see or store your card details. You can also pay with debit or credit card directly through PayPal without a PayPal account.",
            },
            {
              q: "Are Huntheal licenses genuine?",
              a: "100% genuine. Every Huntheal activation key you buy from ProsoftMarketplace activates against the official Huntheal servers with a full year of definition updates and support.",
            },
            {
              q: "How many PCs can one license protect?",
              a: "It depends on the plan you pick: 1 PC ($34.95/year), 3 PCs ($39.95/year), or 5 PCs ($44.95/year). You can install Huntheal on that number of Windows PCs with the same key.",
            },
            {
              q: "What happens when my subscription ends?",
              a: "We'll email you 14 days before your subscription expires. You can renew directly with us at the same low price to keep receiving definition updates and support.",
            },
            {
              q: "Can I upgrade from 1 PC to 3 or 5 PCs later?",
              a: "Yes. Just call +1 (888) 655-4464 or email support@prosoftmarketplace.com and we'll upgrade your key and prorate the difference.",
            },
            {
              q: "Do you offer business or bulk pricing?",
              a: "Yes. For teams that need more than 5 PCs, call +1 (888) 655-4464 for a custom volume quote.",
            },
            {
              q: "How do I contact support?",
              a: "Call +1 (888) 655-4464 (US toll-free) or email support@prosoftmarketplace.com. We're based in Marietta, Georgia and respond to most emails within an hour during business hours.",
            },
          ].map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="flex cursor-pointer items-center justify-between font-display text-base font-bold tracking-tight text-slate-900">
                {f.q}
                <ChevronRight className="h-5 w-5 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-slate-200 bg-emerald-500">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-12 sm:px-6 lg:flex-row lg:items-center lg:px-8 lg:py-16">
          <h2 className="font-display text-3xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            Ready to lock things down?
          </h2>
          <Button
            asChild
            data-testid="final-cta-btn"
            className="rounded-sm bg-slate-900 px-8 py-6 font-bold text-white hover:bg-slate-800"
          >
            <Link to="/products">
              Shop Plans <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
